import React, { useEffect, useState } from "react";
import { ScheduleCampaignModal } from "@common/components/Modal/ScheduleCampaignModal/ScheduleCampaignModal";
import { TabViewWrapper } from "@client/pages/CreateCampaign/CreateCampaign.styled";
import { getStatusColor } from "@common/utils/getStatusColor";
import { AntaresInfeedCampaignStatus } from "@common/typings/common";
import { formatDate } from "@common/utils/date";
import { Value } from "@common/components/Value";
import {
  CAMPAIGN_TITLE,
  SEX_DICTIONARY,
  SexOptions,
} from "@common/consts/campaign";
import { AGE_LIMIT_MIN_MAX_VALUES, Sex } from "@common/typings/campaign";
import { useStores } from "@client/store";
import { Button, Flex, Icon, Spacer, Typography, useTheme } from "@ugc-ui/lego";
import { useIsMobile } from "@common/hooks/isMobile";
import {
  Divider,
  VideoCampaignButton,
  ShowCampaignContainer,
  TypographyDate,
  DescriptionText,
} from "@client/pages/ShowCampaign/ShowCampaign.styled";
import { VideoPlayer } from "@client/pages/ShowCampaign/components/VideoPlayer";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { GetCurrentStatusDate } from "@common/components/GetCurrentStatusDate";
import { Path, PathAdmin } from "@common/consts/Path";
import { Interests } from "@common/components/Interests/Interests";

export const TabViewShowCampaignTabView: React.FC = observer(() => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    client: {
      showCampaignDialogStore: {
        handleStartCampaign: onStartCampaign,
        handleScheduleCampaign,
        handleUnscheduleCampaign,
        isFetching,
        campaignData,
      },
    },
    interestStore,
    countryStore: { attach, options: countryOptions },
    deviceTypeStore: { attach: attachDeviceType, options: deviceTypeOptions },
    ageLimitStore: { attach: attachAgeLimit, options: ageLimitOptions },
  } = useStores();
  const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);

  useEffect(() => {
    attach();
    attachDeviceType();
    attachAgeLimit();
  }, [attach, attachDeviceType, attachAgeLimit]);

  const handleOpenPageEditCampaign = () => {
    navigate(`/${campaignData.uuid}${Path.TO_EDIT}`);
  };

  const handleStartCampaign = () => {
    onStartCampaign(() => {
      navigate(PathAdmin.TO_HOME);
    });
  };

  const isStartableStatus = [
    AntaresInfeedCampaignStatus.PLANNED,
    AntaresInfeedCampaignStatus.PAUSED,
  ].includes(campaignData.status.id);
  const isPlanned =
    campaignData.status.id === AntaresInfeedCampaignStatus.PLANNED;
  const buttonStart = isStartableStatus && (
    <>
      {isPlanned &&
        (campaignData.scheduledStartAt ? (
          <Button
            theme={theme}
            color="default"
            variant="outlined"
            onClick={handleUnscheduleCampaign}
          >
            Отменить отложенный запуск
          </Button>
        ) : (
          <Button
            theme={theme}
            color="default"
            variant="outlined"
            onClick={() => setScheduleModalOpen(true)}
          >
            Запустить позже
          </Button>
        ))}
      <Button theme={theme} onClick={handleStartCampaign}>
        Запустить
      </Button>
    </>
  );

  return (
    <TabViewWrapper>
      <Flex flexGrow={1} flexDirection="column" justifyContent="space-between">
        <ShowCampaignContainer theme={theme} flexDirection="column">
          <Flex flexDirection="column">
            <Flex justifyContent="space-between">
              <Flex flexDirection="column" dataCy="дата создана" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  Создана
                </Typography>
                <TypographyDate color={theme.gray.a80} type="body2">
                  {formatDate(campaignData.dateCreate)}
                </TypographyDate>
              </Flex>
              {GetCurrentStatusDate(campaignData).map((item) => (
                <Flex
                  flexDirection="column"
                  key={item.text}
                  dataCy={`дата ${item.text}`}
                  gap={8}
                >
                  <Typography color={theme.gray.a50} type="caption">
                    {item.text}
                  </Typography>
                  <TypographyDate
                    color={item.color || theme.gray.a80}
                    type="body2"
                  >
                    {item.date}
                  </TypographyDate>
                </Flex>
              ))}
            </Flex>

            <Divider theme={theme} />
            <Flex gap={24}>
              {isMobile && (
                <VideoPlayer
                  currentCampaign={campaignData}
                  isShowPreviewBtn={false}
                />
              )}
              <Flex
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                gap={22}
                flex="2 1"
              >
                {!isMobile && (
                  <Flex flexDirection="column" gap={16} flexGrow={2}>
                    <Flex
                      flexDirection="column"
                      gap={8}
                      dataCy="ссылка на рекламный материал"
                    >
                      <Typography color={theme.gray.a50} type="caption">
                        Ссылка на рекламный материал
                      </Typography>
                      <Typography
                        component="a"
                        color={theme.gray.a80}
                        type="body2"
                        href={campaignData.productLink}
                        target="_blank"
                      >
                        <Value>
                          {campaignData.productLink || <span>&mdash;</span>}
                        </Value>
                      </Typography>
                    </Flex>
                  </Flex>
                )}
                <Flex flexDirection="column" gap={16} flexGrow={1}>
                  <Flex flexDirection="column" gap={8}>
                    <Typography color={theme.gray.a50} type="caption">
                      Показы (текущие / заданные)
                    </Typography>
                    <Typography color={theme.gray.a80} type="body2">
                      {campaignData.uniqueViewsCount?.toLocaleString()} /{" "}
                      {campaignData.viewsLimitCount?.toLocaleString()}
                    </Typography>
                  </Flex>

                  <Flex flexDirection="column" gap={8}>
                    <Typography color={theme.gray.a50} type="caption">
                      Клики
                    </Typography>
                    <Typography color={theme.gray.a80} type="body2">
                      {campaignData.uniqueClickCount?.toLocaleString() || 0}
                    </Typography>
                  </Flex>

                  <Flex flexDirection="column" gap={8}>
                    <Typography color={theme.gray.a50} type="caption">
                      CTR
                    </Typography>
                    <Typography color={theme.gray.a80} type="body2">
                      <Value>{campaignData.ctr}</Value>
                    </Typography>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <Divider theme={theme} />

            <div>
              {[
                AntaresInfeedCampaignStatus.PLANNED,
                AntaresInfeedCampaignStatus.BLOCKED,
                AntaresInfeedCampaignStatus.PAUSED,
              ].includes(campaignData.status.id) && (
                <>
                  <Flex justifyContent="space-between" alignItems="center">
                    <VideoCampaignButton
                      theme={theme}
                      onClick={handleOpenPageEditCampaign}
                    >
                      <Icon
                        icon="edit2"
                        size="extraSmall"
                        dataCy="кнопка редактирования кампании"
                        color={theme.gray.a40}
                      />
                      <Typography color={theme.gray.a80} type="body2">
                        Редактировать
                      </Typography>
                    </VideoCampaignButton>
                  </Flex>
                  <Spacer size={24} vertical />
                </>
              )}
            </div>

            <Flex flexDirection="column" gap={16}>
              <Flex flexDirection="column" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  {CAMPAIGN_TITLE.technicalName}
                </Typography>
                <DescriptionText color={theme.gray.a80} type="body2">
                  <Value>{campaignData.technicalName}</Value>
                </DescriptionText>
              </Flex>
              <Flex flexDirection="column" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  {CAMPAIGN_TITLE.name}
                </Typography>
                <DescriptionText color={theme.gray.a80} type="body2">
                  <Value>{campaignData.name}</Value>
                </DescriptionText>
              </Flex>
              <Flex flexDirection="column" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  {CAMPAIGN_TITLE.description}
                </Typography>
                <DescriptionText color={theme.gray.a80} type="body2">
                  <Value>
                    {campaignData.description || <span>&mdash;</span>}
                  </Value>
                </DescriptionText>
              </Flex>
              <Flex flexDirection="column" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  Ссылка на рекламный материал
                </Typography>
                <Typography
                  color={theme.gray.a80}
                  type="body2"
                  component={campaignData.productLink ? "a" : "div"}
                  href={campaignData.productLink}
                  target="_blank"
                >
                  <Value>{campaignData.productLink}</Value>
                </Typography>
              </Flex>
              <Flex flexDirection="column" gap={8}>
                <Typography color={theme.gray.a50} type="caption">
                  {CAMPAIGN_TITLE.uniqueShow}
                </Typography>
                <Typography color={theme.gray.a80} type="body2">
                  <Value>{campaignData.uniqueShow}</Value>
                </Typography>
              </Flex>
            </Flex>

            <Typography
              type="subtitle2"
              color={theme.gray.a100}
              style={{ margin: "24px 0" }}
            >
              Таргетинг
            </Typography>

            <Flex flexDirection="column" gap={16}>
              <Flex justifyContent="space-between" gap={8} flex="2 1">
                <Flex flexDirection="column" gap={8} flexGrow={2}>
                  <Typography color={theme.gray.a50} type="caption">
                    Тип устройства пользователей
                  </Typography>
                  <Typography color={theme.gray.a80} type="body2">
                    <Value>
                      {
                        deviceTypeOptions.find(
                          (opt) => opt.value === campaignData.deviceTypeCode
                        )?.label
                      }
                    </Value>
                  </Typography>
                </Flex>

                <Flex flexDirection="column" gap={8} flexGrow={1}>
                  <Typography color={theme.gray.a50} type="caption">
                    География
                  </Typography>
                  <Typography color={theme.gray.a80} type="body2">
                    <Value>
                      {
                        countryOptions.find(
                          (opt) => opt.value === campaignData.countryIsoCode
                        )?.label
                      }
                    </Value>
                  </Typography>
                </Flex>
              </Flex>
              <Flex justifyContent="space-between" gap={8} flex="2 1">
                <Flex flexDirection="column" gap={8} flexGrow={2}>
                  <Typography color={theme.gray.a50} type="caption">
                    Пол
                  </Typography>
                  <Typography color={theme.gray.a80} type="body2">
                    <Value>
                      {campaignData.sex
                        ? SexOptions.find(
                            (opt) => opt.value === campaignData.sex
                          )?.label
                        : SEX_DICTIONARY[Sex.ALL]}
                    </Value>
                  </Typography>
                </Flex>
                {campaignData.ageLimitFrom !== null &&
                  campaignData.ageLimitTo !== null && (
                    <Flex flexDirection="column" gap={8} flexGrow={1}>
                      <Typography color={theme.gray.a50} type="caption">
                        Возраст
                      </Typography>
                      {campaignData.ageLimitFrom ===
                        AGE_LIMIT_MIN_MAX_VALUES.maxFrom &&
                      campaignData.ageLimitTo ===
                        AGE_LIMIT_MIN_MAX_VALUES.maxTo ? (
                        <Typography color={theme.gray.a80} type="body2">
                          <Value>
                            {`${
                              ageLimitOptions.to.find(
                                (opt) => opt.value === campaignData.ageLimitTo
                              )?.label
                            }`}
                          </Value>
                        </Typography>
                      ) : (
                        <Typography color={theme.gray.a80} type="body2">
                          <Value>
                            {`От ${
                              ageLimitOptions.from.find(
                                (opt) => opt.value === campaignData.ageLimitFrom
                              )?.label
                            } до ${
                              ageLimitOptions.to.find(
                                (opt) => opt.value === campaignData.ageLimitTo
                              )?.label
                            }`}
                          </Value>
                        </Typography>
                      )}
                    </Flex>
                  )}
              </Flex>
              <Flex>
                <Interests
                  preselected={campaignData.selectedInterests}
                  editable={false}
                  interestStore={interestStore}
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex
            gap={24}
            style={{ paddingTop: "16px" }}
            dataCy="контейнер кнопок"
            justifyContent="space-space-evenly"
          >
            {buttonStart}
          </Flex>
        </ShowCampaignContainer>
      </Flex>
      <ScheduleCampaignModal
        isOpen={isScheduleModalOpen}
        isFetching={isFetching}
        initialIso={campaignData.scheduledStartAt}
        onClose={() => setScheduleModalOpen(false)}
        onSubmit={(iso) =>
          handleScheduleCampaign(iso, () => setScheduleModalOpen(false))
        }
      />

      <Flex flexDirection="column">
        {isMobile && (
          <>
            <Typography
              type="subtitle2"
              dataCy="заголовок страницы кампании"
              color={getStatusColor(theme, campaignData.status)}
            >
              {campaignData.status.name}
            </Typography>
            <Spacer size={12} vertical />
          </>
        )}
        {!isMobile && (
          <VideoPlayer currentCampaign={campaignData} isShowPreviewBtn />
        )}
      </Flex>
    </TabViewWrapper>
  );
});
